import { useEffect, useState } from 'preact/hooks';

import { cleanPictoUrl, onPictoSet, pictoSet, pictoUrl, type PictoSet } from './art.js';
import styles from './Pictogram.module.css';

// Eén onderwerp-pictogram uit de set van het actieve thema (feat/themas, docs/ux/themas.md).
// Kleurrijk (Kinderen): <img> van assets/picto. Rustig (Volwassen): <svg><use> naar
// assets/picto-clean/<id>.svg, zodat de lijn currentColor volgt (licht, donker, nacht, thema).
// Beide zijn statische bestanden van dezelfde origin: de tekeningen komen nooit in de JS, en het
// bord laadt alleen de bestanden van de set die op het scherm staat.

/** De set die nu geldt; tekent opnieuw als het bord of de ouderapp van set wisselt. */
export function usePictoSet(): PictoSet {
  const [set, setSet] = useState<PictoSet>(pictoSet);
  useEffect(() => {
    // Gewisseld tussen render en effect: meteen bijwerken.
    if (pictoSet() !== set) setSet(pictoSet());
    return onPictoSet(() => setSet(pictoSet()));
  }, []);
  return set;
}

export interface PictoImgProps {
  /** Een opgelost pictogram-id (resolvePicto). */
  id: string;
  size: number;
  /** Lege tekst = decoratief. */
  alt?: string;
  class?: string | undefined;
  style?: string | undefined;
  /** Alleen voor de kleurrijke set (<img>); de rustige set laadt per bestand al alleen wat nodig is. */
  loading?: 'lazy' | undefined;
  /** Vaste set (bv. het overzicht met beide naast elkaar); anders de actieve. */
  set?: PictoSet | undefined;
}

export function PictoImg({ id, size, alt = '', class: cls, style, loading, set }: PictoImgProps) {
  const active = usePictoSet();
  const which = set ?? active;
  if (which === 'clean') {
    return (
      <svg
        class={cls ? `${styles.clean} ${cls}` : styles.clean}
        style={style}
        width={size}
        height={size}
        viewBox="0 0 48 48"
        role={alt ? 'img' : undefined}
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : 'true'}
        focusable="false"
        data-picto-set="clean"
      >
        <use href={cleanPictoUrl(id)} />
      </svg>
    );
  }
  return (
    <img
      class={cls}
      style={style}
      src={pictoUrl(id)}
      width={size}
      height={size}
      alt={alt}
      loading={loading}
      draggable={false}
    />
  );
}
